import React, { Component } from 'react';
import { AccessBase, AccessCollaboratorJsonModel } from '../../domain/access/BaseAccess';
import { Group, PermissionType, User } from '../../domain/rbac/RbacDomains';
import { CollaboratorType } from '../../constant/Constants';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import FormicaGroupedSearchSelectBox, { FormicaGroupSelectBoxItem } from '../custom/FormicaGroupedSearchSelectBox';
import AnimatedButton from 'components/custom/AnimatedButton';
import LanguageUtil from '../../util/LanguageUtil';

class AccessCreatorComponent extends Component<Props, State> {
  public state = new State();

  public render() {
    const { role, name, selectBoxOptions } = this.state;
    const { access, disabled } = this.props;
    return (
      <div className={'flex w-full items-center justify-between gap-x-4'}>
        <div className={'flex w-[50%] xl:w-full gap-x-3'}>
          <div className={'flex w-full max-w-[50%]'}>
            <FormicaGroupedSearchSelectBox
              key={'name'}
              options={selectBoxOptions}
              value={name}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectUserOrGroup', defaultMessage: 'Select User or Group' })}
              rounded
              ghost={false}
              disabled={disabled}
              onChangeValue={(e: FormicaSelectBoxItem) => this.onChangeName(e)}
              data-testid={'access_creator_name_selectbox'}
            />
          </div>
          <div className={'flex w-full'}>
            <FormicaSearchSelectBox
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.role', defaultMessage: 'Role' })}
              options={this.getRoleOptions()}
              value={role}
              rounded
              disabled={disabled}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeRole(e)}
              data-testid={'access_creator_role_selectbox'}
            />
          </div>
        </div>
        <div className={'flex w-[50%] xl:w-[30%] gap-x-3 items-center justify-items-end'}>
          <div className={`flex`}>
            <AnimatedButton
              onClick={this.addCollaborator}
              customSize={'sm'}
              className={'min-w-[120px] w-full'}
              disabled={disabled}
              icon={'add'}
              text={LanguageUtil.get({ id: 'common.button.invite', defaultMessage: 'Invite' })}
              customType={'primary'}
              data-testid={'access_creator_add_button'}
            />
          </div>
          <div className={'flex w-full max-w-[140px] lg:max-w-full'}>
            <FormicaSearchSelectBox
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.permission', defaultMessage: 'Permission' })}
              label={'Permission'}
              rounded={true}
              icon={'eye'}
              customSize={'medium'}
              disabled={disabled}
              options={this.getPermissionTypes()}
              value={access.permission}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeBasePermission(e)}
              data-testid={'access_base_permission_selectbox'}
            />
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const { users, groups, access } = this.props;

    const filteredUsers = users?.filter((user: User) => {
      return access?.collaborators?.findIndex((collaborator: AccessCollaboratorJsonModel) => collaborator.name == user.username) == -1;
    });
    const filteredGroups = groups?.filter((group: Group) => {
      return access?.collaborators?.findIndex((collaborator: AccessCollaboratorJsonModel) => collaborator.name == group.name) == -1;
    });
    const userOptions: FormicaGroupSelectBoxItem = {
      groupName: 'User',
      items:
        filteredUsers?.map((user: User) => {
          return {
            label: this.checkUserName(user),
            value: user.username,
          };
        }) || [],
    };
    const groupOptions: FormicaGroupSelectBoxItem = {
      groupName: 'Group',
      items:
        filteredGroups?.map((group: Group) => {
          return {
            label: group.name,
            value: group.name,
          };
        }) || [],
    };
    this.setState({ selectBoxOptions: [userOptions, groupOptions] });
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps != this.props) {
      this.calculateFilteredOptions();
    }
  }

  private calculateFilteredOptions = () => {
    const { access, groups, users } = this.props;
    const filteredUsers = users?.filter((user: User) => {
      return access?.collaborators?.findIndex((collaborator: AccessCollaboratorJsonModel) => collaborator.name == user.username) == -1;
    });
    const filteredGroups = groups?.filter((group: Group) => {
      return access?.collaborators?.findIndex((collaborator: AccessCollaboratorJsonModel) => collaborator.name == group.name) == -1;
    });
    const userOptions: FormicaGroupSelectBoxItem = {
      groupName: 'User',
      items:
        filteredUsers?.map((user: User) => {
          return {
            label: this.checkUserName(user),
            value: user.username,
          };
        }) || [],
    };
    const groupOptions: FormicaGroupSelectBoxItem = {
      groupName: 'Group',
      items:
        filteredGroups?.map((group: Group) => {
          return {
            label: group.name,
            value: group.name,
          };
        }) || [],
    };
    this.setState({ selectBoxOptions: [userOptions, groupOptions], name: '' });
  };

  public addCollaborator = () => {
    const { access, changeAccess, disabled } = this.props;
    const { name, role, type } = this.state;
    if (disabled) return;
    if (name != undefined && name.trim().length > 0 && role != undefined && type != undefined && this.checkCollaborator(name)) {
      access.collaborators = [...(access.collaborators || []), { name: name, role: role, type: type }];
      changeAccess(access);
    } else if (!this.checkCollaborator(name)) {
      const index = access?.collaborators?.findIndex((collaborator: AccessCollaboratorJsonModel) => collaborator.name === name);
      if (index != undefined) {
        access.collaborators[index].role = role;
        changeAccess(access);
      }
    }
    this.calculateFilteredOptions();
  };

  public checkCollaborator = (name: string) => {
    const { access } = this.props;
    const collaborator = access?.collaborators?.find((collaborator: AccessCollaboratorJsonModel) => collaborator.name === name);
    return collaborator == undefined;
  };

  private getRoleOptions = () => {
    return [
      { label: LanguageUtil.get({ id: 'common.label.noPermission', defaultMessage: 'No Permission' }), value: 'No Permission' },
      { label: LanguageUtil.get({ id: 'common.label.view', defaultMessage: 'View' }), value: 'View' },
      { label: LanguageUtil.get({ id: 'common.label.edit', defaultMessage: 'Edit' }), value: 'Edit' },
    ];
  };

  private getPermissionTypes = () => {
    return [
      {
        label: LanguageUtil.get({ id: 'common.label.noPermission', defaultMessage: 'No Permission' }),
        value: 'No Permission',
        icon: 'multiplier',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.view', defaultMessage: 'View' }),
        value: 'View',
        icon: 'eye',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.edit', defaultMessage: 'Edit' }),
        value: 'Edit',
        icon: 'edit',
      },
    ];
  };

  private onChangeRole = (e: FormicaSelectBoxItem) => {
    const { disabled } = this.props;
    if (disabled) return;
    this.setState({ role: e.value as PermissionType });
  };

  private onChangeBasePermission = (e: FormicaSelectBoxItem) => {
    const { access, changeAccess, disabled } = this.props;
    if (disabled) return;
    access.permission = e.value as PermissionType;
    changeAccess(access);
  };

  private onChangeName = (e: FormicaSelectBoxItem) => {
    let type = 'User';
    this.state.selectBoxOptions.forEach((group) => {
      group.items.forEach((item) => {
        if (item.value == e.value) {
          type = group.groupName as CollaboratorType;
        }
      });
    });
    const { disabled } = this.props;
    if (disabled) return;
    this.setState({ name: e.value, type: type as CollaboratorType });
  };

  private checkUserName = (user: User) => {
    if (user.firstName != undefined && user.firstName != '' && user.lastName != undefined && user.lastName != '') {
      return user.firstName + ' ' + user.lastName;
    } else {
      return user.username;
    }
  };
}

interface Props {
  access: AccessBase;
  changeAccess: (access: AccessBase) => void;
  disabled?: boolean;
  users: User[];
  groups: Group[];
}

class State {
  role: PermissionType = 'No Permission' as PermissionType;
  name: string = '';
  type: CollaboratorType = 'User';
  selectBoxOptions: FormicaGroupSelectBoxItem[] = [];
}

export default AccessCreatorComponent;
